import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import { get } from 'lodash';

import Button from '../../../../../common/components/Button/Button';
import Toggle from '../../../../../common/components/Toggle/Toggle';
import Echarts from '../../../../../common/components/Echarts';
import { appendRealtimePoint, generateHistoricalSeries, generateRecentSeries, getMetricsProfile } from '../lib/telemetryGenerator';

const MS_10_SECONDS = 10 * 1000;
const DEFAULT_HISTORICAL_DAYS = 3;
const HISTORICAL_STEP_MS = 5 * 60 * 1000;
const REALTIME_WINDOW_MS = 10 * 60 * 1000;

const createLineOption = ({ title, seriesName, data, yAxisLabel, yMin = 0, yMax = 100, formatValue, axisLabelFormatter }) => ({
  baseOption: {
    animation: false,
    animationDurationUpdate: 0,
    animationEasingUpdate: 'linear',
    title: {
      text: title,
      left: 'center',
      textStyle: { fontSize: 14, fontWeight: 500 },
    },
    grid: { top: 40, left: 55, right: 20, bottom: 40 },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'line' },
      valueFormatter: (value) => {
        if (typeof value !== 'number') return value;

        return formatValue ? formatValue(value) : `${value}%`;
      },
    },
    xAxis: {
      type: 'time',
      axisLabel: { hideOverlap: true },
    },
    yAxis: {
      type: 'value',
      min: yMin,
      max: yMax,
      name: yAxisLabel,
      nameGap: 30,
      axisLabel: {
        formatter: axisLabelFormatter || ((value) => `${value}%`),
      },
    },
    series: [
      {
        name: seriesName,
        type: 'line',
        showSymbol: false,
        smooth: true,
        data,
        lineStyle: { width: 2 },
      },
    ],
  },
});

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, padding: '6px 0' }}>
      <Typography variant="body2" color="textSecondary" style={{ minWidth: 160, fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography variant="body2" style={{ textAlign: 'right', flex: 1, wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </div>
  );
}

function EdgeControllerMonitoring({ edgeControllerId, currentEdgeControllerData = null }) {
  const isOnline = get(currentEdgeControllerData, 'online', false);

  const profile = useMemo(() => getMetricsProfile(edgeControllerId), [edgeControllerId]);

  const [isRealtime, setIsRealtime] = useState(false);
  const [isLoadingHistorical, setIsLoadingHistorical] = useState(false);

  const [cpuSeries, setCpuSeries] = useState([]);
  const [ramSeries, setRamSeries] = useState([]);
  const [diskSeries, setDiskSeries] = useState([]);
  const [tempSeries, setTempSeries] = useState([]);

  const realtimeTimer = useRef(null);
  const realtimeInitialLoadTimer = useRef(null);
  const lastRealtimeUpdateAt = useRef(null);
  const realtimeDataRef = useRef({ cpu: [], ram: [], disk: [], temp: [] });

  const clearTimers = React.useCallback(() => {
    if (realtimeTimer.current) {
      clearInterval(realtimeTimer.current);
      realtimeTimer.current = null;
    }
    if (realtimeInitialLoadTimer.current) {
      clearTimeout(realtimeInitialLoadTimer.current);
      realtimeInitialLoadTimer.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isOnline) {
      clearTimers();
      setIsRealtime(false);
      lastRealtimeUpdateAt.current = null;
    }

    return () => {
      clearTimers();
    };
  }, [edgeControllerId, isOnline, clearTimers]);

  const loadHistorical = () => {
    if (!isOnline || isRealtime) {
      return;
    }

    setIsLoadingHistorical(true);

    setTimeout(() => {
      const endMs = Date.now();
      const startMs = endMs - DEFAULT_HISTORICAL_DAYS * 24 * 60 * 60 * 1000;

      const {
        cpuSeries: nextCpu,
        ramSeries: nextRam,
        diskSeries: nextDisk,
        tempSeries: nextTemp,
      } = generateHistoricalSeries({
        profile,
        startMs,
        endMs,
        stepMs: HISTORICAL_STEP_MS,
      });

      setCpuSeries(nextCpu);
      setRamSeries(nextRam);
      setDiskSeries(nextDisk);
      setTempSeries(nextTemp);
      setIsLoadingHistorical(false);
    }, 2500);
  };

  const startRealtime = () => {
    clearTimers();

    realtimeDataRef.current = { cpu: [], ram: [], disk: [], temp: [] };
    setCpuSeries([]);
    setRamSeries([]);
    setDiskSeries([]);
    setTempSeries([]);

    const nowMs = Date.now();
    const {
      cpuSeries: seededCpu,
      ramSeries: seededRam,
      diskSeries: seededDisk,
      tempSeries: seededTemp,
    } = generateRecentSeries({
      profile,
      endMs: nowMs,
      windowMs: REALTIME_WINDOW_MS,
      stepMs: MS_10_SECONDS,
    });

    realtimeDataRef.current = {
      cpu: seededCpu,
      ram: seededRam,
      disk: seededDisk,
      temp: seededTemp,
    };

    setCpuSeries(seededCpu);
    setRamSeries(seededRam);
    setDiskSeries(seededDisk);
    setTempSeries(seededTemp);

    lastRealtimeUpdateAt.current = nowMs;

    realtimeTimer.current = setInterval(() => {
      const timestamp = Date.now();

      const cpu = realtimeDataRef.current.cpu;
      const ramBuffer = realtimeDataRef.current.ram;
      const disk = realtimeDataRef.current.disk;
      const temp = realtimeDataRef.current.temp;

      const lastCpu = cpu.length ? cpu[cpu.length - 1][1] : 0;
      const lastRam = ramBuffer.length ? ramBuffer[ramBuffer.length - 1][1] : 0;
      const lastDisk = disk.length ? disk[disk.length - 1][1] : 0;
      const lastTemp = temp.length ? temp[temp.length - 1][1] : 0;

      const next = appendRealtimePoint({
        profile,
        previous: { cpu: lastCpu, ram: lastRam, diskAvailable: lastDisk, temperature: lastTemp },
      });

      cpu.push([timestamp, next.cpu]);
      ramBuffer.push([timestamp, next.ram]);
      disk.push([timestamp, next.diskAvailable]);
      temp.push([timestamp, next.temperature]);

      lastRealtimeUpdateAt.current = timestamp;

      const trimBefore = timestamp - REALTIME_WINDOW_MS;
      while (cpu.length && cpu[0][0] < trimBefore) cpu.shift();
      while (ramBuffer.length && ramBuffer[0][0] < trimBefore) ramBuffer.shift();
      while (disk.length && disk[0][0] < trimBefore) disk.shift();
      while (temp.length && temp[0][0] < trimBefore) temp.shift();

      setCpuSeries([...cpu]);
      setRamSeries([...ramBuffer]);
      setDiskSeries([...disk]);
      setTempSeries([...temp]);
    }, MS_10_SECONDS);
  };

  const stopRealtime = () => {
    clearTimers();
    lastRealtimeUpdateAt.current = null;
  };

  const handleRealtimeToggle = () => {
    if (!isOnline) {
      return;
    }

    const next = !isRealtime;
    setIsRealtime(next);

    if (next) {
      setIsLoadingHistorical(false);
      startRealtime();
    } else {
      stopRealtime();
    }
  };

  const cpuOption = useMemo(
    () =>
      createLineOption({
        title: 'CPU Usage',
        seriesName: 'CPU',
        data: cpuSeries,
        yAxisLabel: '%',
      }),
    [cpuSeries],
  );

  const ramOption = useMemo(
    () =>
      createLineOption({
        title: 'RAM Usage',
        seriesName: 'RAM',
        data: ramSeries,
        yAxisLabel: '%',
      }),
    [ramSeries],
  );

  const diskOption = useMemo(
    () =>
      createLineOption({
        title: 'Disk Available',
        seriesName: 'Disk',
        data: diskSeries,
        yAxisLabel: '%',
      }),
    [diskSeries],
  );

  const tempOption = useMemo(
    () =>
      createLineOption({
        title: 'Temperature',
        seriesName: 'Temp',
        data: tempSeries,
        yAxisLabel: '°C',
        yMin: 0,
        yMax: 110,
        formatValue: (value) => `${value}°C`,
        axisLabelFormatter: (value) => `${value}°C`,
      }),
    [tempSeries],
  );

  return (
    <div className="monitoring-tab-content" style={{ padding: 16, backgroundColor: '#fff' }}>
      <Grid container spacing={2} alignItems="stretch">

        <Grid item lg={8} md={12}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', md: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={loadHistorical}
                  disabled={!isOnline || isRealtime || isLoadingHistorical}
                >
                  Get Diagnostics
                </Button>

                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2">Real-time</Typography>
                  <Toggle toggled={isRealtime} onToggle={handleRealtimeToggle} />
                </Stack>
              </Stack>
            </Stack>

            {!isOnline ? (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    System Metrics
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    No monitoring data available while edge controller is offline.
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Box sx={{ px: 0 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Card style={{ height: '100%' }}>
                      <CardContent style={{ height: 320 }}>
                        <Echarts option={cpuOption} showLoading={isLoadingHistorical} />
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card style={{ height: '100%' }}>
                      <CardContent style={{ height: 320 }}>
                        <Echarts option={ramOption} showLoading={isLoadingHistorical} />
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card style={{ height: '100%' }}>
                      <CardContent style={{ height: 320 }}>
                        <Echarts option={diskOption} showLoading={isLoadingHistorical} />
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card style={{ height: '100%' }}>
                      <CardContent style={{ height: 320 }}>
                        <Echarts option={tempOption} showLoading={isLoadingHistorical} />
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Stack>
        </Grid>

        <Grid item lg={4} md={12}>
          <Card style={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Hardware Characteristics
              </Typography>

              <Stack spacing={2}>
                <div>
                  <Typography variant="subtitle2" gutterBottom>
                    Core Processing &amp; Memory
                  </Typography>
                  <InfoRow label="Base Unit" value="OnLogic Factor 201" />
                  <InfoRow label="Processor Module" value="Raspberry Pi Compute Module 4 (CM4)" />
                  <InfoRow label="Memory (RAM)" value="8 GB" />
                  <InfoRow label="Onboard Storage" value="32 GB eMMC" />
                </div>

                <div>
                  <Typography variant="subtitle2" gutterBottom>
                    Additional Storage
                  </Typography>
                  <InfoRow label="Primary Drive" value="256 GB M.2 SATA SSD" />
                  <InfoRow label="Specific Model" value="Transcend TS256GMTS970T M.2 2280-D2-B-M" />
                </div>

                <div>
                  <Typography variant="subtitle2" gutterBottom>
                    Connectivity &amp; Ports
                  </Typography>
                  <InfoRow label="Serial Port" value="RS-232/422/485" />
                  <InfoRow label="USB" value="1x USB 3.2 Gen 1 (OS install/boot), 2x USB 2.0" />
                  <InfoRow label="Ethernet" value="1x LAN port (GbE LAN)" />
                  <InfoRow label="Wi‑Fi" value="Integrated Wi‑Fi with Dual Band Terminal Antenna" />
                </div>

                <div>
                  <Typography variant="subtitle2" gutterBottom>
                    Security Hardware
                  </Typography>
                  <InfoRow label="TPM Module" value="Nuvoton NPCT750 TPM 2.0 (Wide Temp)" />
                </div>

                <div>
                  <Typography variant="subtitle2" gutterBottom>
                    Operating System
                  </Typography>
                  <InfoRow label="Supported OS" value="Ubuntu Server 24.04 LTS (64-bit)" />
                </div>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
}

export default EdgeControllerMonitoring;
